/**
 * Shared numerical methods for root finding and intersection detection.
 * Used by roots.creator.ts and intersection.creator.ts.
 */

export interface NumericalConfig {
  /** Tolerance for convergence (default: 1e-6) */
  tolerance: number;
  /** Number of samples to scan for sign changes (default: 200) */
  scanSamples: number;
  /** Maximum number of results to return (default: 10) */
  maxResults: number;
}

export const DEFAULT_NUMERICAL_CONFIG: NumericalConfig = {
  tolerance: 1e-6,
  scanSamples: 200,
  maxResults: 10,
};

/**
 * Bisection method to find a root of f in interval [a, b].
 *
 * Requires that f(a) and f(b) have opposite signs (sign change).
 *
 * @param f - The function to find the root of
 * @param a - Left bound of the interval
 * @param b - Right bound of the interval
 * @param tolerance - Convergence tolerance
 * @param maxIterations - Maximum number of iterations
 * @returns The root, or null if no sign change exists
 */
export function bisection(
  f: (x: number) => number,
  a: number,
  b: number,
  tolerance: number = DEFAULT_NUMERICAL_CONFIG.tolerance,
  maxIterations = 50,
): number | null {
  let fa = f(a);

  if (fa * f(b) > 0) {
    return null;
  }

  for (let i = 0; i < maxIterations; i++) {
    const mid = (a + b) / 2;
    const fmid = f(mid);

    if (Math.abs(fmid) < tolerance || (b - a) / 2 < tolerance) {
      return mid;
    }

    if (fa * fmid < 0) {
      b = mid;
    } else {
      a = mid;
      fa = fmid;
    }
  }

  return (a + b) / 2;
}

/**
 * Find all roots of f(x) = 0 in the given range using sign change detection.
 *
 * Scans the interval for sign changes and uses bisection to refine each root.
 *
 * @param f - The function to find roots of
 * @param xMin - Left bound of the search interval
 * @param xMax - Right bound of the search interval
 * @param config - Optional configuration overrides
 * @returns Array of x-values where f(x) = 0, sorted in ascending order
 */
export function findRoots(
  f: (x: number) => number,
  xMin: number,
  xMax: number,
  config: Partial<NumericalConfig> = {},
): number[] {
  const { tolerance, scanSamples, maxResults } = { ...DEFAULT_NUMERICAL_CONFIG, ...config };
  const roots: number[] = [];
  const step = (xMax - xMin) / scanSamples;

  let prevX = xMin;
  let prevY = f(prevX);

  for (let i = 1; i <= scanSamples && roots.length < maxResults; i++) {
    const x = xMin + i * step;
    const y = f(x);

    if (prevY * y < 0) {
      // Sign change detected - use bisection to find the root
      const root = bisection(f, prevX, x, tolerance);
      if (root !== null && !roots.some((r) => Math.abs(r - root) < tolerance * 10)) {
        // Validate it's a real root, not a discontinuity (e.g., asymptote)
        // At a real root, f(root) should be close to 0
        // At a discontinuity, f(root) will be large (approaching ±∞)
        const fRoot = f(root);
        if (Math.abs(fRoot) < 1) {
          roots.push(root);
        }
      }
    } else if (Math.abs(y) < tolerance && !roots.some((r) => Math.abs(r - x) < tolerance * 10)) {
      // Exact hit (or very close)
      roots.push(x);
    }

    prevX = x;
    prevY = y;
  }

  return roots.sort((a, b) => a - b);
}

/**
 * Find intersection points between two functions (where f(x) = g(x)).
 *
 * Intersections are found by looking for roots of the difference function f(x) - g(x).
 *
 * @param f - First function
 * @param g - Second function
 * @param xMin - Left bound of the search interval
 * @param xMax - Right bound of the search interval
 * @param config - Optional configuration overrides
 * @returns Array of x-values where f(x) = g(x), sorted in ascending order
 */
export function findIntersections(
  f: (x: number) => number,
  g: (x: number) => number,
  xMin: number,
  xMax: number,
  config: Partial<NumericalConfig> = {},
): number[] {
  return findRoots((x) => f(x) - g(x), xMin, xMax, config);
}
